import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, MessageSquare, Calendar as CalendarIcon, Award, ThumbsUp, ThumbsDown, TrendingUp, Search, Loader2, Sparkles, Users, Edit2, Trash2, Eye, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';
import { performanceApi, employeeApi } from '../../services/api';
import { cn } from '../../lib/utils';
import { DeleteConfirmationDialog } from '../ui/delete-confirmation-dialog';

interface EmployeeFeedback {
  id: string;
  subject: string;
  description: string;
  given_by_id: string;
  given_by_name: string;
  given_to_id: string;
  given_to_name: string;
  feedback_type: 'escalation' | 'feedback' | 'positive' | 'negative';
  feedback_date: string;
  score: number;
  deal_id?: string;
  deal_name?: string;
  // Acknowledgement added by a CRM manager via the Escalation popup
  acknowledgement?: string | null;
  is_acknowledged?: boolean;
}

//  Reusable Stats Row
const StatsRow: React.FC<{ feedbacks: EmployeeFeedback[] }> = ({ feedbacks }) => {
  const total = feedbacks.reduce((s, f) => s + f.score, 0);
  const pos = feedbacks.filter(f => f.feedback_type === 'feedback' || f.feedback_type === 'positive' || f.score >= 0).length;
  const neg = feedbacks.filter(f => f.feedback_type === 'escalation' || f.feedback_type === 'negative' || f.score < 0).length;
  const avg = feedbacks.length > 0 ? (total / feedbacks.length).toFixed(1) : '0.0';
  const avgLabel = parseFloat(avg) >= 0 ? `+${avg}` : avg;

  const stats = [
    { label: 'Total Score', value: total >= 0 ? `+${total}` : `${total}`, color: total >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600', bg: 'from-emerald-50/20 to-teal-50/10 dark:from-emerald-950/20 dark:to-slate-900', icon: <Award className="h-6 w-6 text-emerald-600" />, iconBg: 'bg-emerald-100 dark:bg-emerald-900/30', sub: 'Accumulated Score' },
    { label: 'Average Score', value: avgLabel, color: 'text-slate-800 dark:text-white', bg: 'bg-white dark:bg-slate-900', icon: <TrendingUp className="h-6 w-6 text-blue-600" />, iconBg: 'bg-blue-50 dark:bg-blue-900/20', sub: 'Per feedback' },
    { label: 'Feedback', value: `${pos}`, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-white dark:bg-slate-900', icon: <ThumbsUp className="h-6 w-6 text-emerald-500" />, iconBg: 'bg-emerald-50 dark:bg-emerald-950/20', sub: 'Appreciations' },
    { label: 'Escalation', value: `${neg}`, color: 'text-rose-500', bg: 'bg-white dark:bg-slate-900', icon: <ThumbsDown className="h-6 w-6 text-rose-500" />, iconBg: 'bg-rose-50 dark:bg-rose-950/20', sub: 'Escalations' },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map(s => (
        <Card key={s.label} className={cn("border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden hover:translate-y-[-2px] transition-all duration-300", s.bg)}>
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">{s.label}</p>
                <div>
                  <h3 className={cn("text-4xl font-bold tracking-tight leading-none", s.color)}>{s.value}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">{s.sub}</p>
                </div>
              </div>
              <div className={cn("p-3 rounded-2xl shadow-sm", s.iconBg)}>{s.icon}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Reusable Feedback Grid (Table)
const FeedbackGrid: React.FC<{ feedbacks: EmployeeFeedback[]; isLoading: boolean; currentUserId: string; onView: (fb: EmployeeFeedback) => void; onEdit: (fb: EmployeeFeedback) => void; onDelete: (id: string) => void }> = ({ feedbacks, isLoading, currentUserId, onView, onEdit, onDelete }) => {
  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

  if (isLoading) return (
    <div className="flex items-center justify-center h-48">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      <span className="ml-2 font-bold text-slate-400">Loading feedbacks...</span>
    </div>
  );

  if (feedbacks.length === 0) return (
    <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl p-16 text-center">
      <div className="bg-slate-50 dark:bg-slate-800 h-16 w-16 rounded-2xl flex items-center justify-center mb-4 mx-auto">
        <MessageSquare className="h-8 w-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">No feedbacks found</h3>
      <p className="text-sm text-slate-400 mt-1">No feedback entries match the current filter.</p>
    </Card>
  );

  return (
    <div className="w-full bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800/80 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/75 dark:bg-slate-800/50">
              <th className="px-6 py-4 text-xs font-bold text-slate-400 tracking-wider">Date</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-400 tracking-wider">Feedback Details</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-400 tracking-wider">Deal</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-400 tracking-wider">Type</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-400 tracking-wider">Score</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-400 tracking-wider">From</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-400 tracking-wider">To</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-400 tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
            {feedbacks.map(fb => {
              const rawType = (fb.feedback_type || '').toLowerCase();
              const isEscalation = rawType === 'escalation' || rawType === 'negative' || fb.score < 0;
              const typeLabel = fb.feedback_type ? fb.feedback_type.charAt(0).toUpperCase() + fb.feedback_type.slice(1) : 'Feedback';
              return (
                 <tr
                   key={fb.id}
                   className={`transition-all duration-200 ${
                     fb.is_acknowledged
                       ? 'bg-emerald-100/70 dark:bg-emerald-900/60 hover:bg-emerald-200/60 dark:hover:bg-emerald-900/80'
                       : 'hover:bg-slate-50/50 dark:hover:bg-slate-800/20'
                   }`}
                 >
                  {/* Date */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                      <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
                      {formatDate(fb.feedback_date)}
                    </span>
                  </td>

                  {/* Subject & Description preview */}
                  <td className="px-6 py-4 max-w-xs md:max-w-md">
                    <div className="flex flex-col">
                      <span className="font-extrabold text-sm text-[#143A62] dark:text-slate-200 truncate leading-snug">
                        {fb.subject}
                      </span>
                      <span className="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5 max-w-xs font-medium">
                        {fb.description}
                      </span>
                    </div>
                  </td>

                  {/* Deal */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {fb.deal_name ? (
                      <div className="flex items-center gap-1.5">
                        <Briefcase className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 truncate max-w-[130px]">{fb.deal_name}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                    )}
                  </td>

                  {/* Sentiment Badge */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge className={cn(
                      "font-black text-[10px] tracking-wider rounded-xl border-none shadow-none h-6 px-2.5 flex items-center gap-1 w-fit pointer-events-none",
                      isEscalation 
                        ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400' 
                        : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400'
                    )}>
                      {isEscalation ? <ThumbsDown className="h-3 w-3" /> : <ThumbsUp className="h-3 w-3" />}
                      {typeLabel}
                    </Badge>
                  </td>

                  {/* Score */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge className={cn(
                      "font-black text-xs tracking-wider rounded-xl border-none shadow-sm h-6 px-2.5 flex items-center justify-center w-fit pointer-events-none",
                      fb.score > 0 
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' 
                        : 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400'
                    )}>
                      {fb.score > 0 ? `+${fb.score}` : fb.score}
                    </Badge>
                  </td>

                  {/* From Giver */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-slate-300 text-[10px] font-black shrink-0 uppercase">
                        {fb.given_by_name?.charAt(0) || '?'}
                      </div>
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate max-w-[120px]">
                        {fb.given_by_name}
                      </span>
                    </div>
                  </td>

                  {/* To Recipient */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-[#143A62]/10 dark:bg-blue-900/30 flex items-center justify-center text-[#143A62] dark:text-blue-400 text-[10px] font-black shrink-0 uppercase">
                        {fb.given_to_name?.charAt(0) || '?'}
                      </div>
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate max-w-[120px]">
                        {fb.given_to_name}
                      </span>
                    </div>
                  </td>

                  {/* Actions Column */}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="View Details"
                        onClick={() => onView(fb)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      {fb.given_by_id === currentUserId && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            title="Edit Feedback"
                            onClick={() => onEdit(fb)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            title="Delete Feedback"
                            onClick={() => onDelete(fb.id!)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

//Filter Bar
const FilterBar: React.FC<{
  search: string; onSearch: (v: string) => void;
  type: string; onType: (v: any) => void;
}> = ({ search, onSearch, type, onType }) => (
  <div className="bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 p-4 rounded-2xl shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4">
    <div className="space-y-1.5">
      <Label className="text-xs font-bold text-slate-400 tracking-wider">Search</Label>
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
        <Input placeholder="Search subject or description..." value={search} onChange={e => onSearch(e.target.value)}
          className="pl-9 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 font-semibold text-xs h-10 shadow-sm" />
      </div>
    </div>
    <div className="space-y-1.5">
      <Label className="text-xs font-bold text-slate-400 tracking-wider">Feedback Type</Label>
      <Select value={type} onValueChange={onType}>
        <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 font-bold text-xs h-10 rounded-xl shadow-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="rounded-xl">
          <SelectItem value="all" className="text-xs font-bold">All</SelectItem>
          <SelectItem value="escalation" className="text-xs font-bold">Escalation</SelectItem>
          <SelectItem value="feedback" className="text-xs font-bold">Feedback</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

// Main Component
const MyFeedbacks: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { isUserView, canViewTeamPerformance } = useManagerViewPermission();
  const queryClient = useQueryClient();

  // Team tab visible only to managers/admins/HR with Manager View active and permission granted
  const { isManager: authIsManager, isAdmin } = useAuth();
  const isManager = (authIsManager || isAdmin) && !isUserView && canViewTeamPerformance;

  // Per-tab filters
  const [mySearch, setMySearch] = useState('');
  const [myType, setMyType] = useState('all');
  const [teamSearch, setTeamSearch] = useState('');
  const [teamType, setTeamType] = useState('all');

  // Dialog — pre-fill giver from current user
  const currentUserName = user ? `${(user as any).first_name || ''} ${(user as any).last_name || ''}`.trim() || (user as any).username || 'Me' : '';
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewFeedback, setViewFeedback] = useState<EmployeeFeedback | null>(null);
  const [form, setForm] = useState<{
    subject: string;
    description: string;
    given_by_id: string;
    given_to_id: string;
    feedback_type: 'escalation' | 'feedback';
    feedback_date: string;
    score: number;
    deal_id: string;
  }>({
    subject: '',
    description: '',
    given_by_id: user?.id || '',
    given_to_id: '',
    feedback_type: 'feedback',
    feedback_date: new Date().toISOString().split('T')[0],
    score: 5,
    deal_id: ''
  });

  // Fetch all feedbacks
  const { data: allFeedbacks = [], isLoading } = useQuery<EmployeeFeedback[]>({
    queryKey: ['employeeFeedbacks'],
    queryFn: () => performanceApi.employeeFeedbacks.getAll({ limit: 200 }),
  });

  // Fetch employees
  const { data: employeesData = [] } = useQuery({ queryKey: ['employees'], queryFn: () => employeeApi.getAll({}) });
  const employees = Array.isArray(employeesData) ? employeesData : [];

  // Fetch CRM deals for optional deal dropdown
  const { data: crmDeals = [] } = useQuery<any[]>({
    queryKey: ['crmDeals'],
    queryFn: () => performanceApi.crmDeals.getAll(),
    staleTime: 5 * 60 * 1000,
  });

  const [dealSearch, setDealSearch] = useState('');
  const filteredDeals = useMemo(() => {
    if (!dealSearch.trim()) return crmDeals;
    const q = dealSearch.toLowerCase();
    return crmDeals.filter((deal: any) =>
      (deal.title || '').toLowerCase().includes(q) || (deal.stage || '').toLowerCase().includes(q)
    );
  }, [crmDeals, dealSearch]);

  // Only direct reports of the current manager (manager_id === user.id)
  const teamMembers = useMemo(
    () => employees.filter((e: any) => e.manager_id === user?.id),
    [employees, user?.id]
  );

  // Create/Update mutations
  const createMutation = useMutation({
    mutationFn: (data: any) => performanceApi.employeeFeedbacks.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeFeedbacks'] });
      setIsDialogOpen(false);
      setForm({ subject: '', description: '', given_by_id: user?.id || '', given_to_id: '', feedback_type: 'feedback', feedback_date: new Date().toISOString().split('T')[0], score: 5, deal_id: '' });
      toast({ title: 'Feedback saved successfully!' });
    },
    onError: (err: Error) => toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => performanceApi.employeeFeedbacks.update(editingId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeFeedbacks'] });
      setIsDialogOpen(false);
      setEditingId(null);
      setForm({ subject: '', description: '', given_by_id: user?.id || '', given_to_id: '', feedback_type: 'feedback', feedback_date: new Date().toISOString().split('T')[0], score: 5, deal_id: '' });
      toast({ title: 'Feedback updated successfully!' });
    },
    onError: (err: Error) => toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => performanceApi.employeeFeedbacks.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeFeedbacks'] });
      toast({ title: 'Feedback deleted successfully!' });
      setDeleteId(null);
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
      setDeleteId(null);
    },
  });

  const handleEdit = (fb: EmployeeFeedback) => {
    setEditingId(fb.id);
    const rawType = (fb.feedback_type || '').toLowerCase();
    const isEsc = rawType === 'escalation' || rawType === 'negative';
    setForm({
      subject: fb.subject,
      description: fb.description,
      given_by_id: fb.given_by_id,
      given_to_id: fb.given_to_id,
      feedback_type: isEsc ? 'escalation' : 'feedback',
      feedback_date: fb.feedback_date ? fb.feedback_date.split('T')[0] : new Date().toISOString().split('T')[0],
      score: fb.score,
      deal_id: fb.deal_id || '',
    });
    setDealSearch('');
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
  };

  const handleRecipientChange = (id: string) => {
    setForm(p => ({ ...p, given_to_id: id }));
  };

  const handleSubmit = () => {
    if (!form.subject || !form.description || !form.given_by_id || !form.given_to_id || !form.feedback_type) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    const scoreVal = Number(form.score);
    if (form.feedback_type === 'escalation' && (scoreVal > -3 || scoreVal < -10)) {
      toast({ title: 'Error', description: 'Escalation feedback score must be between -10 and -3', variant: 'destructive' });
      return;
    }
    const selectedDeal = form.deal_id ? crmDeals.find((d: any) => d.id === form.deal_id) : null;
    const payload = {
      ...form,
      score: scoreVal,
      feedback_type: form.feedback_type,
      deal_id: form.deal_id || undefined,
      deal_name: selectedDeal?.title || undefined,
    };
    if (editingId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  // Helper filter matcher
  const matchesTypeFilter = (fb: EmployeeFeedback, filterType: string) => {
    if (filterType === 'all') return true;
    const rawFt = (fb.feedback_type || '').toLowerCase();
    if (filterType === 'escalation') {
      return rawFt === 'escalation' || rawFt === 'negative' || fb.score < 0;
    }
    if (filterType === 'feedback') {
      return rawFt === 'feedback' || rawFt === 'positive' || fb.score >= 0;
    }
    return rawFt === filterType.toLowerCase();
  };

  // My Feedbacks = feedbacks RECEIVED by the current user (given_to_id === user.id)
  const myFeedbacks = allFeedbacks.filter(fb => {
    const isRecipient = fb.given_to_id === user?.id;
    const matchSearch = fb.subject.toLowerCase().includes(mySearch.toLowerCase()) || fb.description.toLowerCase().includes(mySearch.toLowerCase());
    const matchType = matchesTypeFilter(fb, myType);
    return isRecipient && matchSearch && matchType;
  });

  // Team Feedbacks = feedbacks for the manager's direct reports (employees where manager_id === user.id)
  const teamMemberIds = useMemo(() => {
    return new Set(
      employees
        .filter((e: any) => e.manager_id === user?.id)
        .map((e: any) => e.id)
    );
  }, [employees, user?.id]);

  const teamFeedbacks = allFeedbacks.filter(fb => {
    const matchSearch = fb.subject.toLowerCase().includes(teamSearch.toLowerCase()) || fb.description.toLowerCase().includes(teamSearch.toLowerCase());
    const matchType = matchesTypeFilter(fb, teamType);
    return matchSearch && matchType;
  });

  const GiveFeedbackButton = (
    <Button onClick={() => {
      setEditingId(null);
      setDealSearch('');
      setForm({ subject: '', description: '', given_by_id: user?.id || '', given_to_id: '', feedback_type: 'feedback', feedback_date: new Date().toISOString().split('T')[0], score: 5, deal_id: '' });
      setIsDialogOpen(true);
    }}>
      <Plus className="h-4 w-4 mr-1.5" /> Give Feedback
    </Button>
  );

  return (
    <div className="h-full overflow-y-auto space-y-6 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight flex items-center gap-2">
           {isManager ? "View your feedbacks and all employee feedback activity" : 'Track and share constructive feedback'}
          </h2>
        </div>
        {isManager && GiveFeedbackButton}
      </div>

      {/* Tabs — managers see 2 tabs, employees only see content directly */}
      {isManager ? (
        <Tabs defaultValue="my-feedbacks" className="w-full space-y-6">
        <TabsList className="bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-1 mb-2 w-fit shadow-sm backdrop-blur-sm">
            <TabsTrigger 
              value="my-feedbacks" 
              className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none"
            >
              <MessageSquare className="h-4 w-4 mr-1.5 inline-block" /> My Feedbacks
            </TabsTrigger>
            <TabsTrigger 
              value="team-feedbacks" 
              className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none"
            >
              <Users className="h-4 w-4 mr-1.5 inline-block" /> All Employee Feedback
            </TabsTrigger>
          </TabsList>

          {/* My Feedbacks Tab */}
          <TabsContent value="my-feedbacks" className="space-y-6">
            <StatsRow feedbacks={myFeedbacks} />
            <FilterBar search={mySearch} onSearch={setMySearch} type={myType} onType={setMyType} />
            <FeedbackGrid feedbacks={myFeedbacks} isLoading={isLoading} currentUserId={user?.id || ''} onView={setViewFeedback} onEdit={handleEdit} onDelete={handleDelete} />
          </TabsContent>

          {/* Team Feedbacks Tab */}
          <TabsContent value="team-feedbacks" className="space-y-6">
            <StatsRow feedbacks={teamFeedbacks} />
            <FilterBar search={teamSearch} onSearch={setTeamSearch} type={teamType} onType={setTeamType} />
            <FeedbackGrid feedbacks={teamFeedbacks} isLoading={isLoading} currentUserId={user?.id || ''} onView={setViewFeedback} onEdit={handleEdit} onDelete={handleDelete} />
          </TabsContent>
        </Tabs>
      ) : (
        /* Employee — just show their feedbacks directly, no tabs */
        <div className="space-y-6">
          <StatsRow feedbacks={myFeedbacks} />
          <FilterBar search={mySearch} onSearch={setMySearch} type={myType} onType={setMyType} />
          <FeedbackGrid feedbacks={myFeedbacks} isLoading={isLoading} currentUserId={user?.id || ''} onView={setViewFeedback} onEdit={handleEdit} onDelete={handleDelete} />
        </div>
      )}

      {/* Give Feedback Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) setEditingId(null);
      }}>
        <DialogContent className="w-[min(96vw,600px)] max-w-[600px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-[#143A62] dark:text-blue-400" /> {editingId ? 'Edit Feedback' : 'Give Feedback'}
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Log manual feedback with score (-10 to +10)
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            {/* Feedback For — only direct reports/employees */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Feedback For *</Label>
              <Select value={form.given_to_id} onValueChange={handleRecipientChange}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-sm h-11 shadow-sm"><SelectValue placeholder="Select team member" /></SelectTrigger>
                <SelectContent className="rounded-xl">
                  {teamMembers.length === 0 ? (
                    <div className="px-3 py-4 text-xs font-bold text-slate-400 text-center">No direct reports found</div>
                  ) : (
                    teamMembers.map((e: any) => <SelectItem key={e.id} value={e.id} className="text-sm font-bold">{e.first_name} {e.last_name}</SelectItem>)
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Subject */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Subject *</Label>
              <Input placeholder="e.g. Exceptional leadership during sprint" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11" />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Description *</Label>
              <Textarea placeholder="Provide detailed feedback..." rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm" />
            </div>

            {/* Feedback type (Mandatory Dropdown - directly after Description) */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Feedback type *</Label>
              <Select
                value={form.feedback_type}
                onValueChange={(val: 'escalation' | 'feedback') => {
                  setForm(p => {
                    let newScore = p.score;
                    if (val === 'escalation') {
                      if (newScore > -3 || newScore < -10) {
                        newScore = -5;
                      }
                    } else {
                      if (newScore < -10 || newScore > 10) {
                        newScore = 5;
                      }
                    }
                    return { ...p, feedback_type: val, score: newScore };
                  });
                }}
              >
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-bold text-sm h-11 shadow-sm">
                  <SelectValue placeholder="Select feedback type" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="escalation" className="text-sm font-bold">Escalation</SelectItem>
                  <SelectItem value="feedback" className="text-sm font-bold">feedback</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Deal (Optional) */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5" /> Related Deal <span className="text-slate-300 font-normal">(Optional)</span>
              </Label>
              <Select value={form.deal_id || 'none'} onValueChange={v => setForm(p => ({ ...p, deal_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-semibold text-sm h-11 shadow-sm">
                  <SelectValue placeholder="Select a deal..." />
                </SelectTrigger>
                <SelectContent className="rounded-xl max-h-60">
                  <div className="p-2 sticky top-0 bg-white dark:bg-slate-900 z-10 border-b border-slate-100 dark:border-slate-800">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        placeholder="Search deal..."
                        value={dealSearch}
                        onChange={e => setDealSearch(e.target.value)}
                        onKeyDown={e => e.stopPropagation()}
                        onPointerDown={e => e.stopPropagation()}
                        className="pl-8 h-8 text-xs font-semibold rounded-lg border-slate-200 dark:border-slate-800 focus:ring-1"
                      />
                    </div>
                  </div>
                  <SelectItem value="none" className="text-sm text-slate-400">- None -</SelectItem>
                  {filteredDeals.length === 0 ? (
                    <div className="p-3 text-xs text-slate-400 text-center font-medium">No deals found</div>
                  ) : (
                    filteredDeals.map((deal: any) => (
                      <SelectItem key={deal.id} value={deal.id} className="text-sm">
                        <span className="font-semibold">{deal.title}</span>
                        {deal.stage && <span className="text-slate-400 ml-2 text-xs">· {deal.stage}</span>}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Date *</Label>
              <Input type="date" value={form.feedback_date} onChange={e => setForm(p => ({ ...p, feedback_date: e.target.value }))}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11" />
            </div>

            {/* Feedback Score */}
            <div className="space-y-3 pt-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Feedback Score *</Label>
                <span className={cn("px-3 py-1 text-sm font-black rounded-lg shadow-sm border leading-none",
                  form.score >= 0 ? "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-400" : "bg-rose-50 text-rose-700 border-rose-100")}
                >
                  {form.score >= 0 ? `+${form.score}` : form.score}
                </span>
              </div>
              <input
                type="range"
                min={form.feedback_type === 'escalation' ? "-10" : "-10"}
                max={form.feedback_type === 'escalation' ? "-3" : "10"}
                value={form.score}
                onChange={e => {
                  const val = Number(e.target.value);
                  setForm(p => ({ ...p, score: val }));
                }}
                className={cn("w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer",
                  form.score >= 0 ? "accent-emerald-600" : "accent-rose-500")}
              />
              <div className="flex justify-between text-[10px] text-slate-400 font-extrabold tracking-tight">
                {form.feedback_type === 'escalation' ? (
                  <>
                    <span>-10 (Critical)</span>
                    <span>-6 (Severe)</span>
                    <span>-3 (Moderate)</span>
                  </>
                ) : (
                  <>
                    <span>-10 (Critical)</span>
                    <span>0 (Neutral)</span>
                    <span>+10 (Excellent)</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending || updateMutation.isPending) ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : (editingId ? 'Update Feedback' : 'Save Feedback')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <DeleteConfirmationDialog 
        isOpen={!!deleteId} 
        onOpenChange={(open) => !open && setDeleteId(null)} 
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        isDeleting={deleteMutation.isPending}
        title="Delete Feedback?"
        description="Are you sure you want to permanently delete this feedback? This action cannot be undone."
      />

      {/* View Details Dialog */}
      <Dialog open={!!viewFeedback} onOpenChange={(open) => !open && setViewFeedback(null)}>
        <DialogContent className="w-[min(96vw,550px)] max-w-[550px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <div className="flex justify-between items-start gap-4">
              <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 leading-snug">
                Feedback Details
              </DialogTitle>
              <Badge className={cn(
                "font-black text-xs tracking-wider rounded-xl border-none shadow-sm h-7 px-3 flex items-center justify-center shrink-0 pointer-events-none",
                viewFeedback?.score && viewFeedback.score > 0 
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' 
                  : 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400'
              )}>
                Score: {viewFeedback?.score && viewFeedback.score > 0 ? `+${viewFeedback.score}` : viewFeedback?.score}
              </Badge>
            </div>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider flex items-center gap-1.5">
              <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
              Logged on {viewFeedback?.feedback_date ? new Date(viewFeedback.feedback_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
            {/* Subject */}
            <div className="space-y-1">
              <span className="text-[10px] text-slate-400 font-bold block">Subject</span>
              <h4 className="text-lg font-extrabold text-[#143A62] dark:text-white leading-snug">
                {viewFeedback?.subject}
              </h4>
            </div>

            {/* Giver and Recipient Side by Side */}
            <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800/40">
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 font-bold block">From (Giver)</span>
                <div className="flex items-center gap-2 mt-1">
                  <div className="h-7 w-7 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-700 dark:text-slate-300 text-xs font-black uppercase">
                    {viewFeedback?.given_by_name?.charAt(0) || '?'}
                  </div>
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300 truncate">
                    {viewFeedback?.given_by_name}
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 font-bold block">To (Recipient)</span>
                <div className="flex items-center gap-2 mt-1">
                  <div className="h-7 w-7 rounded-full bg-[#143A62]/10 dark:bg-blue-900/30 flex items-center justify-center text-[#143A62] dark:text-blue-400 text-xs font-black uppercase">
                    {viewFeedback?.given_to_name?.charAt(0) || '?'}
                  </div>
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300 truncate">
                    {viewFeedback?.given_to_name}
                  </span>
                </div>
              </div>
            </div>

            {/* Sentiment & Details Card */}
            <div className="space-y-2">
              <span className="text-[10px] text-slate-400 font-bold block">Feedback Type</span>
              {(() => {
                const rawFt = (viewFeedback?.feedback_type || '').toLowerCase();
                const isEsc = rawFt === 'escalation' || rawFt === 'negative' || (viewFeedback?.score !== undefined && viewFeedback.score < 0);
                const label = viewFeedback?.feedback_type ? viewFeedback.feedback_type.charAt(0).toUpperCase() + viewFeedback.feedback_type.slice(1) : '';
                return (
                  <Badge className={cn(
                    "font-black text-xs tracking-wider rounded-xl border-none shadow-none h-7 px-3 flex items-center gap-1.5 w-fit pointer-events-none",
                    isEsc 
                      ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400' 
                      : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400'
                  )}>
                    {isEsc ? <ThumbsDown className="h-3.5 w-3.5" /> : <ThumbsUp className="h-3.5 w-3.5" />}
                    {label}
                  </Badge>
                );
              })()}
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <span className="text-[10px] text-slate-400 font-bold block">Description</span>
              <div className={cn(
                "p-4 rounded-xl border bg-white dark:bg-slate-900 text-sm font-medium text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap shadow-inner",
                (viewFeedback?.feedback_type === 'escalation' || viewFeedback?.feedback_type === 'negative' || (viewFeedback?.score !== undefined && viewFeedback.score < 0))
                  ? 'border-l-4 border-l-rose-500 border-slate-100 dark:border-slate-800'
                  : 'border-l-4 border-l-emerald-500 border-slate-100 dark:border-slate-800'
              )}>
                {viewFeedback?.description}
              </div>
            </div>

            {/* Related Deal */}
            {viewFeedback?.deal_name && (
              <div className="space-y-1.5">
                <span className="text-[10px] text-slate-400 font-bold block">Related Deal</span>
                <div className="flex items-center gap-2.5 p-3 rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-950/20">
                  <Briefcase className="h-4 w-4 text-blue-500 shrink-0" />
                  <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">{viewFeedback.deal_name}</span>
                </div>
              </div>
            )}

            {/* Acknowledgement — filled by the CRM manager */}
            {viewFeedback?.is_acknowledged && viewFeedback.acknowledgement && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400 font-bold block tracking-wider uppercase">Acknowledgement</span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                    ✓ Acknowledged by Manager
                  </span>
                </div>
                <div className="p-4 rounded-xl border border-l-4 border-l-emerald-500 border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 text-sm font-medium text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap shadow-inner">
                  {viewFeedback.acknowledgement}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0">
            <Button onClick={() => setViewFeedback(null)}>
              Close Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyFeedbacks;
